import { useEffect, useRef, useState } from 'react';

const CURSIVE_FONTS = [
  { label: 'Signature 1', value: '"Dancing Script", cursive' },
  { label: 'Signature 2', value: '"Great Vibes", cursive' },
  { label: 'Signature 3', value: '"Pacifico", cursive' },
];

const formatTs = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const parseValue = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

// ── Canvas draw tab ─────────────────────────────────────────────────────────
const DrawPad = ({ onCapture, onClear, isEmpty }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e1b4b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - r.left, y: src.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    onClear();
  };

  const capture = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onCapture(dataUrl);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={340}
        height={130}
        className="rounded-xl w-full touch-none"
        style={{ border: '1px solid rgba(99,102,241,0.2)', cursor: 'crosshair', display: 'block' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex justify-between mt-2">
        <button type="button" onClick={clear} className="text-[12px] px-3 py-1 rounded-lg" style={{ color: '#6b7280', border: '1px solid #e5e7eb' }}>
          Clear
        </button>
        <button
          type="button"
          onClick={capture}
          disabled={!hasStrokes}
          className="text-[12px] px-4 py-1 rounded-lg font-semibold text-white transition-colors"
          style={{ background: hasStrokes ? '#4f46e5' : 'rgba(99,102,241,0.3)', cursor: hasStrokes ? 'pointer' : 'not-allowed' }}
        >
          Use Signature
        </button>
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const SignatureCustomField = ({ field, updateField }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('type');
  const [typedName, setTypedName] = useState('');
  const [fontIdx, setFontIdx] = useState(0);
  const panelRef = useRef(null);

  const sig = parseValue(field?.value);

  const handleClickOutside = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) close();
  };

  const close = () => {
    setOpen(false);
    document.removeEventListener('mousedown', handleClickOutside);
  };

  const openPanel = () => {
    setTypedName('');
    setTab('type');
    setOpen(true);
    setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
  };

  const save = (value, mode) => {
    const payload = JSON.stringify({ mode, value, signedAt: new Date().toISOString() });
    updateField(field._id, payload);
    close();
  };

  const clear = () => updateField(field._id, null);

  return (
    <div className="relative w-full px-3 py-2" style={{ minHeight: 40 }}>
      {/* ── Display ── */}
      {sig ? (
        <div className="flex items-center gap-2 group">
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={openPanel}
            title="Click to re-sign"
          >
            {sig.mode === 'drawn' ? (
              <img src={sig.value} alt="Signature" className="max-h-10 max-w-full object-contain" style={{ borderBottom: '1px solid rgba(30,27,75,0.2)' }} />
            ) : (
              <span style={{ fontFamily: CURSIVE_FONTS[fontIdx]?.value, fontSize: 22, color: '#1e1b4b', lineHeight: 1.2, display: 'block' }}>
                {sig.value}
              </span>
            )}
            <div className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: 'rgba(15,15,25,0.35)' }}>
              <svg width="9" height="9" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 3.5v4.25l2.5 2.5-1.06 1.06-2.94-2.94V4.5h1.5z"/></svg>
              Signed {formatTs(sig.signedAt)}
            </div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 text-[11px] px-1.5 py-0.5 rounded"
            style={{ color: '#dc2626', background: 'rgba(220,38,38,0.07)' }}
            title="Clear signature"
          >✕</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPanel}
          className="flex items-center gap-1.5 text-[13px]"
          style={{ color: 'rgba(15,15,25,0.28)' }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15.232 5.232l3.536 3.536M9 13l6.536-6.536a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z"/>
          </svg>
          Add signature…
        </button>
      )}

      {/* ── Panel ── */}
      {open && (
        <div
          ref={panelRef}
          className="absolute z-50 left-0 bg-white rounded-2xl shadow-2xl"
          style={{ top: 'calc(100% + 6px)', width: 370, border: '1px solid rgba(99,102,241,0.15)', overflow: 'hidden' }}
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-3" style={{ borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
            <div className="text-[13px] font-semibold mb-3" style={{ color: 'rgba(15,15,25,0.8)' }}>Add Signature</div>
            <div className="flex gap-0 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(99,102,241,0.15)' }}>
              {['type', 'draw'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className="flex-1 py-1.5 text-[12px] font-semibold capitalize transition-colors"
                  style={tab === t
                    ? { background: '#4f46e5', color: 'white' }
                    : { background: 'white', color: 'rgba(15,15,25,0.5)' }
                  }
                >
                  {t === 'type' ? 'Type' : 'Draw'}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4">
            {tab === 'type' ? (
              <div>
                {/* Font selector */}
                <div className="flex gap-1.5 mb-3">
                  {CURSIVE_FONTS.map((f, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setFontIdx(i)}
                      className="flex-1 py-1.5 rounded-lg text-[11px] transition-colors"
                      style={fontIdx === i
                        ? { border: '1.5px solid #4f46e5', background: 'rgba(99,102,241,0.06)', color: '#4f46e5' }
                        : { border: '1px solid rgba(99,102,241,0.15)', color: 'rgba(15,15,25,0.45)', background: 'white' }
                      }
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Preview */}
                <div
                  className="rounded-xl mb-3 flex items-center justify-center px-4"
                  style={{ height: 80, border: '1px solid rgba(99,102,241,0.15)', background: 'rgba(99,102,241,0.02)' }}
                >
                  <span style={{ fontFamily: CURSIVE_FONTS[fontIdx].value, fontSize: 28, color: typedName ? '#1e1b4b' : 'rgba(15,15,25,0.18)', lineHeight: 1.2 }}>
                    {typedName || 'Your signature…'}
                  </span>
                </div>

                <input
                  autoFocus
                  type="text"
                  value={typedName}
                  onChange={e => setTypedName(e.target.value)}
                  placeholder="Type your name"
                  className="w-full h-9 px-3 text-[13px] rounded-xl outline-none mb-3"
                  style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.8)' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.2)'}
                  onKeyDown={e => { if (e.key === 'Enter' && typedName.trim()) save(typedName.trim(), 'typed'); }}
                />

                <button
                  type="button"
                  disabled={!typedName.trim()}
                  onClick={() => save(typedName.trim(), 'typed')}
                  className="w-full py-2 rounded-xl text-[13px] font-semibold text-white transition-colors"
                  style={{ background: typedName.trim() ? '#4f46e5' : 'rgba(99,102,241,0.3)', cursor: typedName.trim() ? 'pointer' : 'not-allowed' }}
                >
                  Save Signature
                </button>
              </div>
            ) : (
              <DrawPad
                onCapture={dataUrl => save(dataUrl, 'drawn')}
                onClear={() => {}}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCustomField;
